import React from 'react';
import {useTranslation} from 'react-i18next';
import {CheckCircle2, ThumbsUp, Clock, ShieldAlert, Ban} from 'lucide-react';
import type {StandingStep, UserRestrictionDTO} from './AccountStanding';

interface StandingProgressProps {
    data: UserRestrictionDTO;
    steps: StandingStep[];
}

const StandingProgress = ({data, steps}: StandingProgressProps) => {
    const {t} = useTranslation();

    const currentStepIndex = steps.findIndex(step => step.level === data.standing);

    const getStandingDescription = (level: StandingStep['level']) => {
        return t(`dashboard.profile.standing.tooltip.${level.toLowerCase()}`);
    };

    const getPenaltyByLevel = (level: StandingStep['level']) => {
        switch (level) {
            case 'EXCELLENT':
                return t('dashboard.profile.standing.penalty_pardon');
            case 'GOOD':
                return t('dashboard.profile.standing.penalty_1day');
            case 'POOR':
                return t('dashboard.profile.standing.penalty_3days');
            case 'AT_RISK':
                return t('dashboard.profile.standing.penalty_ban');
            case 'BANNED':
                return t('dashboard.profile.standing.penalty_is_banned');
            default:
                return '';
        }
    };

    const iconByLevel = {
        EXCELLENT: <CheckCircle2 size={20}/>,
        GOOD: <ThumbsUp size={20}/>,
        POOR: <Clock size={20}/>,
        AT_RISK: <ShieldAlert size={20}/>,
        BANNED: <Ban size={20}/>
    };

    return (
        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-start mb-10 gap-6 md:gap-0">
            {steps.map((step, index) => {
                const isActive = index === currentStepIndex;
                const isPast = index < currentStepIndex;
                const isLast = index === steps.length - 1;

                return (
                    <div key={step.level} className="flex-1 flex flex-row md:flex-col items-center relative">

                        {!isLast && (
                            <div className="absolute top-6 left-[50%] right-[-50%] h-0.5 pointer-events-none hidden md:block px-10">
                                <div className={`w-full h-full ${isPast ? 'bg-blue-500/30' : 'bg-neutral-200 dark:bg-white/5'}`}/>
                            </div>
                        )}

                        {!isLast && (
                            <div className="absolute left-6 top-12 w-0.5 h-8 pointer-events-none block md:hidden">
                                <div className={`w-full h-full ${isPast ? 'bg-blue-500/30' : 'bg-neutral-200 dark:bg-white/5'}`}/>
                            </div>
                        )}

                        <div className="relative z-10 flex flex-col items-center group">
                            <div className="absolute bottom-full mb-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 pointer-events-none min-w-[240px] text-center z-20 left-14 md:left-1/2 md:-translate-x-1/2">
                                <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-white/10 rounded-xl p-3 shadow-2xl">
                                    <p className={`text-[10px] font-black mb-1 uppercase tracking-widest ${step.color}`}>
                                        {step.label}
                                    </p>

                                    <p className="text-[11px] text-gray-500 dark:text-slate-400 leading-snug mb-2">
                                        {getStandingDescription(step.level)}
                                    </p>

                                    <div className="pt-2 border-t border-neutral-100 dark:border-white/5">
                                        <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase">
                                            {t('dashboard.profile.standing.penalty_prefix')}
                                        </p>

                                        <p className="text-[10px] font-black dark:text-white uppercase italic">
                                            {getPenaltyByLevel(step.level)}
                                        </p>
                                    </div>
                                </div>

                                <div className="w-2.5 h-2.5 bg-white dark:bg-neutral-800 border-r border-b border-neutral-200 dark:border-white/10 rotate-45 mx-auto -mt-1.5 hidden md:block"/>
                            </div>

                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 border-2 shrink-0 bg-neutral-50 dark:bg-neutral-900 ${
                                isActive
                                    ? `shadow-xl scale-110 ${step.color} border-current ring-4 ring-current/5`
                                    : 'border-neutral-200 dark:border-white/10 text-gray-300 dark:text-slate-800'
                            }`}>
                                {iconByLevel[step.level]}
                            </div>

                            <span className={`absolute top-full mt-3 text-[9px] font-black uppercase tracking-[0.15em] transition-colors whitespace-nowrap hidden md:block ${
                                isActive
                                    ? 'text-light-text dark:text-white'
                                    : 'text-gray-400 dark:text-slate-600'
                            }`}>
                                {step.label}
                            </span>
                        </div>

                        <span className={`ml-6 text-[9px] font-black uppercase tracking-[0.15em] transition-colors block md:hidden ${
                            isActive
                                ? 'text-light-text dark:text-white'
                                : 'text-gray-400 dark:text-slate-600'
                        }`}>
                            {step.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default StandingProgress;